/**
 * O motivo que o backend deu, ou `null`.
 *
 * As rotas de controle recusam com `detail` acionável: "já há um run em curso neste
 * pacote", "transição inválida de cancelling para paused". Jogar isso fora e não mostrar
 * nada é o que faz alguém clicar de novo achando que não funcionou.
 *
 * ⚠️ **Três estados, e a diferença entre dois deles é o ponto.** `null` é "não houve
 * erro"; `""` é "houve erro e não veio texto"; a string é o motivo. Colapsar `""` em
 * `null` apagaria a caixa e recriaria o silêncio que esta função existe para acabar, e
 * quem chama põe a frase traduzida no lugar do vazio.
 *
 * Mora em `lib/` e não dentro de um componente (#593): o hook do logon precisa do mesmo
 * extrator, e importar de um componente para dentro de um hook inverte a dependência.
 */
export function errorDetail(error: unknown): string | null {
  if (!error) return null;
  const detail = backendDetail(error);
  if (detail) return detail;
  const msg = (error as { message?: unknown })?.message;
  if (typeof msg === "string" && msg.trim()) return msg;
  return "";
}

/**
 * O `detail` do envelope, ou `null`. O único lugar que conhece o formato.
 *
 * `detail` nem sempre é string: o `422` do FastAPI devolve uma **lista** de erros
 * de validação, e renderizar isso põe `[object Object]` na tela. A checagem de
 * tipo não é paranoia de TypeScript, é o caso real.
 */
function backendDetail(error: unknown): string | null {
  const detail = rawDetail(error);
  return typeof detail === "string" && detail.trim() ? detail : null;
}

/**
 * O `code` da recusa, ou `null`.
 *
 * Algumas rotas recusam com `detail = {"code", "message"}`, no padrão do
 * `_conflict` da Calibragem (a decisão por WRICEF, #1343, é uma). O `code` é
 * fixo e em inglês, e é por ele que a tela escolhe a frase traduzida: o
 * `message` vem em português fixo. `detail` em texto, ou a lista do `422` do
 * FastAPI, não tem código.
 */
export function detailCode(error: unknown): string | null {
  const detail = rawDetail(error);
  if (detail === null || typeof detail !== "object" || Array.isArray(detail)) return null;
  const code = (detail as { code?: unknown }).code;
  return typeof code === "string" && code ? code : null;
}

/** O `detail` do envelope do axios, como veio. */
function rawDetail(error: unknown): unknown {
  return (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
}

/**
 * O status HTTP da resposta, ou `undefined` quando não houve resposta.
 *
 * A metade simétrica do `backendDetail`: os dois leem o **mesmo** envelope do
 * axios, e a extração do `detail` já tinha sido centralizada aqui enquanto a do
 * `status` seguia copiada à mão em três arquivos (o guard de rota, a política de
 * retry e o store de auth). Envelope conhecido em dois lugares é o mesmo defeito
 * de antes, com metade dos call sites.
 *
 * `undefined` é informação, não ausência de dado: quer dizer que a requisição não
 * chegou a receber resposta — queda de rede, timeout, DNS. Quem decide política
 * de repetição precisa exatamente dessa distinção, então ela não colapsa em `0`
 * nem em `null`.
 */
export function statusOf(error: unknown): number | undefined {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return typeof status === "number" ? status : undefined;
}

/**
 * O motivo que o backend deu, ou a frase de quem chamou. **Nunca** a do axios.
 *
 * Irmã do `errorDetail`, e a diferença é uma linha: esta não cai em
 * `error.message`. Existe porque as duas precisões são legítimas e opostas.
 *
 * O Code Companion prefere `message` a caixa vazia — ali a alternativa é o
 * silêncio, e "Network Error" ainda diz alguma coisa. As telas de conta,
 * workspace e projeto **têm** frase pronta e traduzida para o caso de não vir
 * `detail`; para elas, `message` de um erro do axios é `"Request failed with
 * status code 400"` — inglês, cru, e sem informação nenhuma para quem lê.
 *
 * Nasceu de cinco cópias deste mesmo `?? fallback` escritas à mão (achado C1).
 * O que se ganha não é linha a menos: é o envelope conhecido num lugar só, para
 * o dia em que o backend mudar o formato.
 */
export function errorDetailOr(error: unknown, fallback: string): string {
  return backendDetail(error) ?? fallback;
}
